import {Component, EventEmitter, Inject, OnInit, Output} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Observable} from 'rxjs';
import {User} from '@core/models/user.model';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {debounceTime, distinctUntilChanged, tap} from 'rxjs/operators';
import { CollaboratorsInvitationModel } from 'app/modules/wizard/models/collaborators-invitation.model';

@Component({
    selector: 'hmt-invite-collaborators',
    templateUrl: './invite-collaborators.component.html',
    styleUrls: ['./invite-collaborators.component.scss'],
    standalone: false
})
export class InviteCollaboratorsComponent implements OnInit {

  collaboratorsForm: FormGroup;
  selectedCollaborators: User[] = [];
  errorMessage: string = '';

  @Output() inviteCollaboratorsEmitter: EventEmitter<CollaboratorsInvitationModel> = new EventEmitter();
  @Output() searchCollaborator: EventEmitter<string> = new EventEmitter();


  constructor(
    private dialogRef: MatDialogRef<InviteCollaboratorsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      internalCollaborators: Observable<User[]>,
      existingCollaborators?: Array<{ user: string; email: string; name: string }>
    }
  ) { }

  ngOnInit(): void {
    this.initForm();
  }

  initForm() {
    this.collaboratorsForm = new FormGroup({
      internalCollaborators: new FormControl(''),
    });

    this.collaboratorsForm.get('internalCollaborators')
      .valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(1000),
        tap((searchText: string) => {
          this.searchCollaborator.emit(searchText);
        })
      )
      .subscribe();
  }

  displayWithCollaborator(user: User): string {
    if (!user) {
      return '';
    }
    return `${user.firstName} ${user.lastName}`;
  }

  onSelectCollaborator(collaborator: User): void {
    this.collaboratorsForm.controls['internalCollaborators'].patchValue(null);
    this.errorMessage = '';
    
    const index = this.selectedCollaborators.findIndex(c => c.id == collaborator.id);
    if (index >= 0) {
      this.errorMessage = `${collaborator.firstName} ${collaborator.lastName} is already selected`;
      return;
    }
    
    const collaboratorUserId = collaborator.userId;
    const collaboratorId = collaborator.id;
    const collaboratorIndividualId = collaborator.individualId;
    
    const existingCollaborator = this.data.existingCollaborators?.find(
      ec => ec.user === collaboratorUserId || 
            ec.user === collaboratorId || 
            ec.user === collaboratorIndividualId
    );
    
    if (existingCollaborator) {
      this.errorMessage = `${collaborator.firstName} ${collaborator.lastName} is already a collaborator`;
      return;
    }
    
    this.selectedCollaborators.push(collaborator);
  }

  removeCollaborator(index: number): void {
    this.selectedCollaborators.splice(index, 1);
    this.errorMessage = '';
  }

  inviteCollaborators(): void {
    if (this.selectedCollaborators.length === 0) {
      this.errorMessage = 'Please select at least one collaborator to invite';
      return;
    }
    
    if (!this.collaboratorsForm.valid) {
      Object.keys(this.collaboratorsForm.controls).forEach(field => {
        const control = this.collaboratorsForm.get(field);
        control.markAsTouched({ onlySelf: true });
      });
      return;
    }
    
    const collaboratorsInvitationModel: CollaboratorsInvitationModel = {
      collaborators: this.selectedCollaborators.map(ic => ic.id)
    };
    this.inviteCollaboratorsEmitter.emit(collaboratorsInvitationModel);
    this.close();
  }

  close(): void {
    this.dialogRef.close();
  }

}
